import React, { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Route, Routes, Link, useLocation } from 'react-router-dom';
import { ChainProvider } from './contexts/ChainContext';
import { BlockchainServiceProvider } from './contexts/BlockchainServiceContext';
import { GlobalDataProvider } from './contexts/GlobalDataContext';
import Header from './components/Header';
import SearchBand from './components/SearchBand';
import Footer from './components/Footer';
import Monitor from './pages/Monitor';
import favicon from './assets/favicon.ico';
import PageLoader from './components/PageLoader';
import { WalletAccountProvider } from './contexts/WalletAccountContext';
import EmptyState from './components/EmptyState';
import { SearchX, Users } from 'lucide-react';

// Cada página se descarga al visitarla; la portada va en el bundle principal
const Blocks = lazy(() => import('./pages/Blocks'));
const Producers = lazy(() => import('./pages/Producers'));
const Market = lazy(() => import('./pages/Market'));
const AccountDetail = lazy(() => import('./pages/AccountDetail'));
const Transaction = lazy(() => import('./components/Transaction'));
const Block = lazy(() => import('./components/Block'));
const TransferTokens = lazy(() => import('./pages/wallet/TransferTokens'));
const Voters = lazy(() => import('./pages/wallet/Voters'));
const Resources = lazy(() => import('./pages/wallet/Resources'));
const Keys = lazy(() => import('./pages/wallet/Keys'));
const CreateAccount = lazy(() => import('./pages/wallet/CreateAccount'));
const MSIG = lazy(() => import('./pages/MSIG'));
const KeyAccounts = lazy(() => import('./pages/KeyAccounts'));
const Chain = lazy(() => import('./pages/Chain'));
const Token = lazy(() => import('./pages/Token'));
const ProposalDetails = lazy(() => import('./pages/ProposalDetails'));
const TotalAmount = lazy(() => import('./pages/TopHolders/TotalAmount'));
const RAM = lazy(() => import('./pages/TopHolders/RAM'));
const REX = lazy(() => import('./pages/TopHolders/REX'));
const WalletLayout = lazy(() => import('./components/wallet/WalletLayout'));
const GlobalCacheDebug = lazy(() => import('./components/GlobalCacheDebug'));

function App() {
  useEffect(() => {
    // Agregar el favicon
    const link = document.createElement('link');
    link.rel = 'icon';
    link.href = favicon;
    document.head.appendChild(link);

    // Limpieza al desmontar el componente
    return () => {
      document.head.removeChild(link);
    };
  }, []);

  return (
    <ChainProvider>
      <BlockchainServiceProvider>
        <GlobalDataProvider>
          <WalletAccountProvider>
            <Router>
              <AppContent />
            </Router>
          </WalletAccountProvider>
        </GlobalDataProvider>
      </BlockchainServiceProvider>
    </ChainProvider>
  );
}

function AppContent() {
  const location = useLocation();

  useEffect(() => {
    const pageTitle = getPageTitle(location.pathname);
    document.title = `${pageTitle} | ChainScout`;
  }, [location]);

  return (
    <div className="min-h-screen flex flex-col bg-canvas">
      <Header />
      {/* La portada (Monitor) pinta su propio buscador grande */}
      {location.pathname !== '/' && <SearchBand />}

        <main className="flex-1 pb-12">
          <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<Monitor />} />
            <Route path="/blocks" element={<Blocks />} />
            <Route path="/explorer" element={<Blocks />} />
            <Route path="/producers" element={<Producers />} />
            <Route path="/wallet/transfer" element={<WalletLayout title="Transfer"><TransferTokens /></WalletLayout>} />
            <Route path="/market" element={<Market />} />
            <Route path="/account-detail/:accountName" element={<AccountDetail />} />
            {/* Sin cuenta en la URL (enlace del sidebar sin login) */}
            <Route path="/account-detail" element={
              <div className="container py-6">
                <EmptyState icon={Users} title="No account selected" message="Search for an account above, or log in to see your own." />
              </div>
            } />
            <Route path="/wallet/vote" element={<WalletLayout title="Vote"><Voters /></WalletLayout>} />
            <Route path="/wallet/cpu-net-ram" element={<WalletLayout title="Resources"><Resources /></WalletLayout>} />
            <Route path="/transaction/:id" element={<Transaction />} />
            <Route path="/block/:num" element={<Block />} />
            <Route path="/wallet/keys" element={<WalletLayout title="Permissions"><Keys /></WalletLayout>} />
            <Route path="/wallet/create-account" element={<WalletLayout title="Create account"><CreateAccount /></WalletLayout>} />
            <Route path="/key/:publicKey" element={<KeyAccounts />} />
            <Route path="/chain" element={<Chain />} />
            <Route path="/token/:contract/:symbol" element={<Token />} />
            <Route path="/multisig" element={<MSIG />} />
            <Route path="/proposal/:proposer/:proposalName" element={<ProposalDetails />} />
            {/* Añadir las nuevas rutas para Top Holders */}
            <Route path="/top-holders/total" element={<TotalAmount />} />
            <Route path="/top-holders/ram" element={<RAM />} />
            <Route path="/top-holders/rex" element={<REX />} />
            <Route path="*" element={
              <div className="container py-6">
                <EmptyState icon={SearchX} title="Page not found" message="This page doesn't exist.">
                  <Link to="/" className="link">Back to Home</Link>
                </EmptyState>
              </div>
            } />
          </Routes>
          </Suspense>
        </main>

      <Footer />

      {/* Global Cache Debug Component */}
      {process.env.NODE_ENV === 'development' && <Suspense fallback={null}><GlobalCacheDebug /></Suspense>}
    </div>
  );
}

function getPageTitle(pathname) {
  if (pathname.startsWith('/block/')) return 'Block';
  if (pathname.startsWith('/transaction')) return 'Transaction';
  if (pathname.startsWith('/proposal')) return 'Proposal';
  if (pathname.startsWith('/key/')) return 'Public key';
  if (pathname.startsWith('/account-detail')) return pathname.split('/')[2] || 'Account Detail';
  switch (pathname) {
    case '/':
      return 'Monitor';
    case '/blocks':
    case '/explorer':
      return 'Latest Blocks';
    case '/producers':
      return 'Block Producers';
    case '/wallet/transfer':
      return 'Transfer Tokens';
    case '/market':
      return 'Market';
    case '/chain':
      return 'Chain';
    case '/wallet/vote':
      return 'Vote';
    case '/wallet/cpu-net-ram':
      return 'Resources';
    case '/wallet/keys':
      return 'Permissions';
    case '/wallet/create-account':
      return 'Create Account';
    case '/multisig':
      return 'Multisigs';
    // Añadir los nuevos títulos para Top Holders
    case '/top-holders/total':
      return 'Top Token Holders';
    case '/top-holders/ram':
      return 'Top RAM Holders';
    case '/top-holders/rex':
      return 'Top REX Holders';
    default:
      return 'Blockchain Explorer and Wallet';
  }
}

export default App;
